import { api, cachedApi, getHeaderHashForTheUncachable } from '@store/apihelpers';
import { CONTENTTYPES, RELATION_TYPES } from '@utils/curriculumHelper';
import { getVskoReqHeader } from '@utils/utils';
import { last } from 'lodash-es';
import type {
  ApiContentRelation,
  ApiContentWith$$Relations,
  CurriculumRoot,
  LVLlinkidCurriculum,
  LVLlinkidGoal,
  LlinkidGoalCorrelations,
} from '../../types/contentApiTypes';

export const CONTENTAPI_DOCUMENT_KEYS = {
  LEIDRAAD: '3b754443-dc47-4ebf-8ec5-58d6da24eabe',
  EDUCATIONAL_POINTERS: '8f39910a-5db7-4306-9443-74a318aba0da',
  EDUCATIONAL_COMPONENTS: '8669afe3-aa83-437b-b624-742e93e8de5a',
  INTRODUCTION_GRADE: {
    1: 'f6370bbd-6a0a-41e8-9b9b-c342e1cd0c45',
    2: 'a9beef4f-3bd4-4b73-87f8-d7c83c149fb5',
    3: '7a7d03c1-da3d-450f-97a6-10ed4170c124',
    7: '11dce64c-2555-4e50-805f-68843c71a06c',
  },
} as const;

export async function getMinimalCurricula(): Promise<CurriculumRoot[]> {
  const baseCurricula = await cachedApi.getAll(
    '/content/',
    { type: CONTENTTYPES.curriculum, limit: 5000, expand: 'summary' },
    getVskoReqHeader({
      headers: getHeaderHashForTheUncachable(false),
    }),
  );
  return baseCurricula.filter((elem) => elem.issued);
}

export async function getCurriculaGoalLists(): Promise<ApiContentWith$$Relations[]> {
  const goalLists = await cachedApi.getAll(
    '/content',
    { type: CONTENTTYPES.goalList, limit: 5000 },
    getVskoReqHeader({
      headers: getHeaderHashForTheUncachable(false),
    }),
  );
  return goalLists;
}

export async function getCurriculaRelations(): Promise<ApiContentRelation[]> {
  const curriculumRelations = await cachedApi.getAll(
    '/content/relations',
    {
      contentType: CONTENTTYPES.curriculum,
      limit: 50000,
      relationtype: RELATION_TYPES.replaces,
    },
    getVskoReqHeader({
      headers: getHeaderHashForTheUncachable(false),
    }),
  );

  return curriculumRelations;
}

// export async function getCurriculumCorrelations({ from, to }) {
//   const curriculumCorrelations = await api.getRaw(`/content/curriculumCorrelation`, {
//     from: `/content/${from}`,
//     to: `/content/${to}`,
//   });

//   return curriculumCorrelations;
// }

export async function getLlinkidGoalCorrelations(): Promise<LlinkidGoalCorrelations> {
  const curriculumCorrelations = await cachedApi.getRaw('/content/llinkidGoalCorrelation');
  return curriculumCorrelations;
}

function appendHashToAttachments(items: ApiContentWith$$Relations[], hash: string): void {
  items.forEach((item) => {
    item.attachments?.forEach((att) => {
      att.href = `${att.href}?hash=${hash}`;
    });
  });
}

async function getCachedContent(
  key: string,
  refresh: boolean | undefined,
  params: Record<string, unknown>,
  addHashToAttachments = true,
): Promise<ApiContentWith$$Relations[]> {
  const hashTimestamp = refresh ? `?t=${Math.round(Date.now() / 1000)}` : '';
  const hash = await cachedApi.get(`/content/${key}/hash${hashTimestamp}`);
  const response = await cachedApi.getAll(
    '/content',
    params,
    getVskoReqHeader({
      headers: { 'VSKO-Resource-Hash': hash.hash },
    }),
  );
  if (addHashToAttachments) {
    appendHashToAttachments(response, hash.hash);
  }
  return response;
}

export async function getDocument(key: string, refresh: boolean | undefined): Promise<ApiContentWith$$Relations[]> {
  const root = key;
  return getCachedContent(root, refresh, {
    root,
    limit: 5000,
  });
}

export async function getCurriculum(keyOrHref: string, refresh: boolean): Promise<LVLlinkidCurriculum> {
  const key = keyOrHref.includes('/') ? last(keyOrHref.split('/')) : keyOrHref; // in case of href, get the key.
  let response;
  if (refresh) {
    response = await api.get(`/content/${key}/llinkidCurricula/preview`);
  } else {
    const hash = await cachedApi.get(`/content/${key}/llinkidCurricula/hash`);
    response = await cachedApi.get(
      `/content/${key}/llinkidCurricula`,
      {},
      getVskoReqHeader({
        headers: { 'VSKO-Resource-Hash': hash.hash },
      }),
    );
  }

  return response;
}

export async function getGoal(key: string, refresh: boolean): Promise<LVLlinkidGoal> {
  if (refresh) {
    return api.get(`/content/${key}/llinkidGoal/preview`);
  }
  const hash = await cachedApi.get(`/content/${key}/llinkidGoal/hash`);
  return cachedApi.get(
    `/content/${key}/llinkidGoal`,
    {},
    getVskoReqHeader({
      headers: { 'VSKO-Resource-Hash': hash.hash },
    }),
  );
}
